import { useState, useEffect, memo } from "react";
import React from "react";
import { SvgArrowRight, SvgChecked, SvgCamera } from "ui/svgs";
import { useParams } from "react-router-dom";
import clsx from "clsx";
import { showToast } from "ui/components/common";
import { WsResDataType } from "lib/types";
import { useCustomersService } from "pages/dashboard/customers/service";
import type { WsCameraSettingReq } from "pages/dashboard/customers/service";
import { getCamerasBySiteIds } from "lib/actions";
import { CamerasRes, CameraInfo } from "lib/types";
import { Tooltip, Table, Tag, Select } from "antd";
import type { TableColumnsType, TableProps } from "antd";
import { SvgBoxUnChecked } from "ui/svgs";
import PubSub from "pubsub-js";
import "./setting-table.scss";

const { Column, ColumnGroup } = Table;
type TableRowSelection<T extends object = object> =
  TableProps<T>["rowSelection"];
type DataType = CameraInfo & { submiting?: boolean };

function CameraSettingTable({
  siteId,
  active,
  reCalcParentHeight,
  onChange,
}: {
  siteId: number;
  active: boolean;
  reCalcParentHeight: (height: number) => void;
  onChange?: (siteId: number, cameras: CameraInfo[]) => void;
}) {
  console.log("render CameraSettingTable", siteId);
  const { orgid } = useParams();
  const orgId = orgid!;

  const refContent = React.useRef<HTMLDivElement>(null);
  const { sendUpdateCameraSettingRequest } = useCustomersService();

  const [cameras, setCameras] = useState<DataType[]>();
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const init = async () => {
    try {
      setLoading(true);
      const res = await getCamerasBySiteIds(orgid, siteId.toString(), 0, 300);
      const data = (res as CamerasRes)?.data;
      setCameras(data);
      return data;
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (active && !Array.isArray(cameras)) {
      init();
    }
  }, [active]);

  useEffect(() => {
    console.log("reCalcParentHeight = ", refContent?.current?.scrollHeight);
    if (cameras?.length > 0) {
      reCalcParentHeight(refContent?.current?.scrollHeight);
    } else {
      reCalcParentHeight(0);
    }
  }, [cameras]);

  useEffect(() => {
    const reset = () => {
      setSelectedIds([]);
    };
    const subscription = PubSub.subscribe("resetBatchEncodeCameras", reset);
    return () => {
      PubSub.unsubscribe("resetBatchEncodeCameras", subscription);
    };
  }, []);

  const rowSelection: TableRowSelection<DataType> = {
    onChange: (selectedRowKeys, selectedRows) => {
      console.log(
        `selectedRowKeys: ${selectedRowKeys}`,
        "selectedRows: ",
        selectedRows,
      );
      setSelectedIds(selectedRows?.map((row) => +row.id));
      PubSub.publish("encodeCamerasSelectedChanged", {
        siteId,
        cameras: selectedRows,
      });
      //onChange?.(siteId, selectedRows);
    },
    selectedRowKeys: selectedIds,
    getCheckboxProps: (record) => ({
      disabled: record.submiting || record?.status != "online",
    }),
  };

  const handleCameraSetting = async (camera: DataType) => {
    const curCamera = cameras?.find((c) => c.id === camera.id);
    if (!curCamera) {
      return;
    }
    curCamera.submiting = true;
    setCameras([...cameras]);

    const cameraSetting: WsCameraSettingReq = {
      id: +camera.id,
      name: camera.name,
      mainStream: {
        bitRate: camera.mainStream.bitRate,
        frameRate: camera.mainStream.frameRate,
        resolution: {
          height: camera.mainStream.resolution.height,
          width: camera.mainStream.resolution.width,
          name: camera.mainStream.resolution.name,
        },
      },
      subStream: {
        bitRate: camera.subStream.bitRate,
        frameRate: camera.subStream.frameRate,
        resolution: {
          height: camera.subStream.resolution.height,
          width: camera.subStream.resolution.width,
          name: camera.subStream.resolution.name,
        },
      },
    };

    try {
      const res = await sendUpdateCameraSettingRequest([cameraSetting], orgId);
      const data = (res as WsResDataType)?.deviceUpdateCamerasSettingsReply
        ?.data;

      if (
        (data?.length > 0 &&
          !data?.at(0)?.nameRes?.error &&
          !data?.at(0)?.streamSettingsRes?.error) ||
        data?.length === 0
      ) {
        showToast(0, "The Camera has been updated successfully!");
      } else {
        showToast(1, "Failed to update the Camera. Please try again later.");
      }
    } catch (error) {
      console.error(error);
      showToast(1, "Failed to update the Camera. Please try again later.");
    }

    curCamera.submiting = false;
    setCameras([...cameras]);
  };

  return (
    <div className="encoding-table bg-neutral-50 px-4" ref={refContent}>
      {!Array.isArray(cameras) && (
        <button
          className="absolute left-4 z-10"
          style={{ top: "calc(50% - 7px )" }}
          onClick={async (e) => {
            e.stopPropagation();
            e.preventDefault();
            const data = await init();
            const filterEnabled = data?.filter(
              (row) => row?.status == "online",
            );
            if (filterEnabled?.length > 0) {
              setSelectedIds(filterEnabled.map((row) => +row.id));
              PubSub.publish("encodeCamerasSelectedChanged", {
                siteId,
                cameras: filterEnabled,
              });
            }
          }}
        >
          <SvgBoxUnChecked width="15" className="fill-neutral-500 mr-1" />
        </button>
      )}
      {Array.isArray(cameras) && (
        <Table
          loading={loading}
          dataSource={cameras}
          rowSelection={{ ...rowSelection }}
          rowKey="id"
          pagination={false}
          rowClassName={() => {
            return "group";
          }}
        >
          <ColumnGroup title="Camera Information">
            <Column
              title="Device Name"
              dataIndex="name"
              render={(name) => (
                <div className="flex items-center">
                  <SvgCamera
                    width="20"
                    className="fill-neutral-500 mr-2 flex-shrink-0"
                  />
                  <p className="min-w-6">{name}</p>
                </div>
              )}
            />
            <Column
              title="Status"
              dataIndex="status"
              width={120}
              render={(status: string) => (
                <div
                  className="rounded-2xl text-sm text-center py-0.5"
                  style={{
                    backgroundColor: status == "online" ? "#E4F6EC" : "#FFE2E0",
                  }}
                >
                  <span
                    className={clsx(
                      "inline-block mb-0.5 w-[6px] h-[6px] rounded-full mr-1",
                      status == "online" ? "bg-[#3A855D]" : "bg-red-500",
                    )}
                  ></span>
                  <span
                    className={clsx(
                      status == "online" ? "text-[#3A855D]" : "text-red-500",
                    )}
                  >
                    {status}
                  </span>
                </div>
              )}
            />
          </ColumnGroup>
          <ColumnGroup title="HD">
            <Column
              title="Resolution"
              render={(_, record: DataType, index) => {
                return (
                  <Select
                    disabled={record?.submiting}
                    className="table-select"
                    popupClassName="select-dropdown"
                    style={{ width: "100%", borderRadius: "16px" }}
                    dropdownStyle={{ borderRadius: "16px", padding: "4px 0" }}
                    suffixIcon={
                      <SvgArrowRight
                        className={clsx(
                          "rotate-90",
                          record?.submiting
                            ? "fill-neutral-300"
                            : "fill-neutral-500 ",
                        )}
                        width="16"
                      />
                    }
                    value={record?.mainStream?.resolution?.name}
                    onChange={(value) => {
                      const resolution =
                        record?.capabilities?.mainStream?.resolutions?.find(
                          (resolution) => resolution.name === value,
                        );
                      let newBiteRate;
                      // cur > dafault > first
                      if (
                        resolution?.bitRates?.includes(
                          record?.mainStream?.bitRate,
                        )
                      ) {
                        newBiteRate = record?.mainStream?.bitRate;
                      } else if (
                        resolution?.bitRates?.includes(
                          resolution?.defaultBitRate,
                        )
                      ) {
                        newBiteRate = resolution?.defaultBitRate;
                      } else {
                        newBiteRate = resolution?.bitRates?.at(0);
                      }
                      cameras[index].mainStream = {
                        resolution,
                        bitRate: newBiteRate,
                        frameRate: resolution?.frameRates?.at(0),
                      };

                      setCameras([...cameras]);
                    }}
                    options={record?.capabilities?.mainStream?.resolutions?.map(
                      (resolution) => {
                        return {
                          value: resolution.name,
                          label: resolution.name,
                        };
                      },
                    )}
                  />
                );
              }}
            />
            <Column
              title="Frame Rate"
              render={(_, record: DataType, index) => {
                return (
                  <Select
                    className="table-select"
                    popupClassName="select-dropdown"
                    style={{ width: "100%", borderRadius: "16px" }}
                    dropdownStyle={{ borderRadius: "16px", padding: "4px 0" }}
                    disabled={record?.submiting}
                    suffixIcon={
                      <SvgArrowRight
                        className={clsx(
                          "rotate-90",
                          record?.submiting
                            ? "fill-neutral-300"
                            : "fill-neutral-500 ",
                        )}
                        width="16"
                      />
                    }
                    value={record?.mainStream?.frameRate}
                    onChange={(value) => {
                      cameras[index].mainStream.frameRate = value;
                      setCameras([...cameras]);
                    }}
                    options={record?.capabilities?.mainStream?.resolutions
                      ?.find(
                        (r) => r?.name == record?.mainStream?.resolution?.name,
                      )
                      ?.frameRates?.map((frameRate) => {
                        return {
                          value: frameRate,
                          label: frameRate,
                        };
                      })}
                  />
                );
              }}
            />
            <Column
              title="Bit Rate"
              render={(_, record: DataType, index) => {
                return (
                  <Select
                    className="table-select"
                    popupClassName="select-dropdown"
                    style={{ width: "100%", borderRadius: "16px" }}
                    dropdownStyle={{ borderRadius: "16px", padding: "4px 0" }}
                    disabled={record?.submiting}
                    suffixIcon={
                      <SvgArrowRight
                        className={clsx(
                          "rotate-90",
                          record?.submiting
                            ? "fill-neutral-300"
                            : "fill-neutral-500 ",
                        )}
                        width="16"
                      />
                    }
                    value={record?.mainStream?.bitRate}
                    onChange={(value) => {
                      cameras[index].mainStream.bitRate = value;
                      setCameras([...cameras]);
                    }}
                    options={record?.capabilities?.mainStream?.resolutions
                      ?.find(
                        (r) => r?.name == record?.mainStream?.resolution?.name,
                      )
                      ?.bitRates?.map((bitRate) => {
                        return {
                          value: bitRate,
                          label: bitRate,
                        };
                      })}
                  />
                );
              }}
            />
          </ColumnGroup>
          <ColumnGroup title="SD">
            <Column
              title="Resolution"
              render={(_, record: DataType, index) => {
                return (
                  <Select
                    className="table-select"
                    popupClassName="select-dropdown"
                    style={{ width: "100%", borderRadius: "16px" }}
                    dropdownStyle={{ borderRadius: "16px", padding: "4px 0" }}
                    disabled={record?.submiting}
                    suffixIcon={
                      <SvgArrowRight
                        className={clsx(
                          "rotate-90",
                          record?.submiting
                            ? "fill-neutral-300"
                            : "fill-neutral-500 ",
                        )}
                        width="16"
                      />
                    }
                    value={record?.subStream?.resolution?.name}
                    onChange={(value) => {
                      const resolution =
                        record?.capabilities?.subStream?.resolutions?.find(
                          (resolution) => resolution.name === value,
                        );

                      let newBiteRate;
                      // cur > dafault > first
                      if (
                        resolution?.bitRates?.includes(
                          record?.mainStream?.bitRate,
                        )
                      ) {
                        newBiteRate = record?.mainStream?.bitRate;
                      } else if (
                        resolution?.bitRates?.includes(
                          resolution?.defaultBitRate,
                        )
                      ) {
                        newBiteRate = resolution?.defaultBitRate;
                      } else {
                        newBiteRate = resolution?.bitRates?.at(0);
                      }
                      cameras[index].subStream = {
                        resolution,
                        bitRate: newBiteRate,
                        frameRate: resolution?.frameRates?.at(0),
                      };

                      setCameras([...cameras]);
                    }}
                    options={record?.capabilities?.subStream?.resolutions?.map(
                      (resolution) => {
                        return {
                          value: resolution.name,
                          label: resolution.name,
                        };
                      },
                    )}
                  />
                );
              }}
            />
            <Column
              title="Frame Rate"
              render={(_, record: DataType, index) => {
                return (
                  <Select
                    className="table-select"
                    popupClassName="select-dropdown"
                    style={{ width: "100%", borderRadius: "16px" }}
                    dropdownStyle={{ borderRadius: "16px", padding: "4px 0" }}
                    disabled={record?.submiting}
                    suffixIcon={
                      <SvgArrowRight
                        className={clsx(
                          "rotate-90",
                          record?.submiting
                            ? "fill-neutral-300"
                            : "fill-neutral-500 ",
                        )}
                        width="16"
                      />
                    }
                    value={record?.subStream?.frameRate}
                    onChange={(value) => {
                      cameras[index].subStream.frameRate = value;
                      setCameras([...cameras]);
                    }}
                    options={record?.capabilities?.subStream?.resolutions
                      ?.find(
                        (r) => r?.name == record?.subStream?.resolution?.name,
                      )
                      ?.frameRates?.map((frameRate) => {
                        return {
                          value: frameRate,
                          label: frameRate,
                        };
                      })}
                  />
                );
              }}
            />
            <Column
              title="Bit Rate"
              render={(_, record: DataType, index) => {
                return (
                  <Select
                    className="table-select"
                    popupClassName="select-dropdown"
                    style={{ width: "100%", borderRadius: "16px" }}
                    dropdownStyle={{ borderRadius: "16px", padding: "4px 0" }}
                    disabled={record?.submiting}
                    suffixIcon={
                      <SvgArrowRight
                        className={clsx(
                          "rotate-90",
                          record?.submiting
                            ? "fill-neutral-300"
                            : "fill-neutral-500 ",
                        )}
                        width="16"
                      />
                    }
                    value={record?.subStream?.bitRate}
                    onChange={(value) => {
                      cameras[index].subStream.bitRate = value;
                      setCameras([...cameras]);
                    }}
                    options={record?.capabilities?.subStream?.resolutions
                      ?.find(
                        (r) => r?.name == record?.subStream?.resolution?.name,
                      )
                      ?.bitRates?.map((bitRate) => {
                        return {
                          value: bitRate,
                          label: bitRate,
                        };
                      })}
                  />
                );
              }}
            />
          </ColumnGroup>
          <Column
            title=""
            render={(_: any, record: DataType) =>
              record?.submiting ? (
                <div className="w-10 h-10 flex items-center justify-center">
                  <div
                    className="ml-1 text-blue-500  inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
                    role="status"
                  ></div>
                </div>
              ) : (
                <Tooltip title="confirm">
                  <div className="w-10 h-10 rounded-full overflow-hidden invisible group-hover:visible">
                    <button
                      disabled={record?.status != "online"}
                      className={clsx(
                        "flex items-center justify-center w-full h-full  hover:bg-blue-100 disabled:cursor-not-allowed",
                      )}
                      onClick={() => {
                        handleCameraSetting(record);
                      }}
                    >
                      <SvgChecked width="15" className="fill-[#3E83F6]" />
                    </button>
                  </div>
                </Tooltip>
              )
            }
          />
        </Table>
      )}
    </div>
  );
}

export default memo(CameraSettingTable);
